// "Verify it's you" — RON-5465. Prototype region: `.verify-content`.
//
// The gate in front of the pack, and the reason the emailed link is not a credential.
//
// §1: the link carries a bare guid that never expires and travels in forwarded mail. The guid says
// WHICH pack; the code proves WHO. `request-code` mails the code to the address ON THE PACK —
// never one in the request — so holding the link is not enough; you need the mailbox.
//
// TWO THINGS THIS SCREEN MUST NOT DO, both of which would leak one customer's data to another:
//
// BOTH of the things this screen must not do were lifted on 24-October-2026, by the same change.
//
// They were: never say whether the guid is real, and never show the email even masked. Both rested
// on `request-code` answering identically for a live pack, an unknown guid and a finished one — so
// anything specific on this screen would have told a stranger holding a forwarded link that the
// guid was real.
//
// Phase A's `open` runs BEFORE `request-code` and 404s an unknown token while naming the customer
// for a live one, so the server draws that distinction itself. `request-code` is unchanged and
// still tells you nothing; it is simply no longer the first thing that happens.
//
// WHAT HAS NOT CHANGED: this screen never masks an address itself. The server sends `masked_email`
// already masked, to a FIXED length — "one star per hidden character gives away the address's
// length, which helps a guesser and tells the contractor nothing". The real address never arrives
// here, so there is nothing to mask and nothing to leak by accident.

import { useState } from "react";
import { useTranslation } from "react-i18next";

const CODE_LENGTH = 6;

// §1: five code requests per hour, per contractor. The prototype counts them out loud ("request 2
// of 5") and that is worth keeping: somebody who has asked twice can see where the ceiling is
// before they hit it, and a contractor locked out for an hour cannot get onto site.
const MAX_CODE_REQUESTS = 5;

const PortalVerify = ({
  company,
  linkKind,
  maskedEmail,
  contact,
  isBusy,
  error,
  codeRequested,
  codeRequestCount,
  onRequestCode,
  onVerify,
}) => {
  const { t } = useTranslation("common");
  const [code, setCode] = useState("");

  // Step A until a code has been asked for, step B after.
  //
  // CORRECTED 06-October-2026. This used to be `Boolean(maskedEmail)`, on the belief that the
  // masked address was "the evidence that the request went through". The real `request-code`
  // returns `{ sent: true }` and no address at all, so against the live API this was permanently
  // false and the code box NEVER APPEARED — the portal would have been a dead end for everyone.
  //
  // The prototype gates on a plain boolean too (`verifyState.codeSent`), so this also matches it.
  const hasRequested = codeRequested === true;

  const onCodeChange = (value) => {
    // Digits only, capped. A code pasted from an email often arrives with a stray space, and
    // stripping it is kinder than refusing it.
    setCode(value.replace(/\D/g, "").slice(0, CODE_LENGTH));
  };

  const canSubmit = code.length === CODE_LENGTH && !isBusy;

  return (
    <div className="portal-verify">
      <div className="portal-verify__card">
        <span className="portal-verify__icon" aria-hidden="true">
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {/* A shield, as the prototype has. The padlock that was here read as "locked out"
                rather than "protected", which is the opposite of the reassurance intended. */}
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <path d="M9 12l2 2 4-4" />
          </svg>
        </span>

        <h1 className="portal-verify__title">{t("react_portal_verify_title")}</h1>

        {/* Three whole sentences rather than one assembled from fragments: a translator needs the
            name in the place their language puts it, which is not always the end. The nameless
            wording stays for the case where `open` sent no company — an older server, or a reply
            we could not read. */}
        <p className="portal-verify__sub">
          {company
            ? t(
                linkKind === "renewal"
                  ? "react_portal_verify_sub_renewal"
                  : "react_portal_verify_sub_preapproval"
              ).replace("%s", company)
            : t("react_portal_verify_sub")}
        </p>

        {!hasRequested ? (
          <>
            <div className="portal-verify__well">
              <p className="portal-verify__well-label">{t("react_portal_verify_send_to")}</p>
              {/* Rendered EXACTLY as the server sent it. Masking happens server-side, and the
                  wording falls back to the vague form when `masked_email` is null — which the
                  contract says means the address is unusable, so there is nothing honest to
                  show. */}
              <p className="portal-verify__email">
                {maskedEmail || t("react_portal_verify_your_email")}
              </p>
            </div>

            <button
              type="button"
              className="portal-verify__btn"
              disabled={isBusy}
              onClick={onRequestCode}
            >
              {t(isBusy ? "react_loading" : "react_portal_send_code")}
            </button>

            {error && <p className="portal-verify__error">{error}</p>}

            {/* The way out for somebody whose email on file is wrong — the one person this
                screen used to be unable to help at all, because they cannot sign in to find out
                who to ask. `contact` arrives on a live link as of 24-October.

                Still falls back to "reply to your invitation" when there is no sender on record,
                which is what a system-sent renewal looks like. */}
            {contact?.name && contact?.email ? (
              <p className="portal-verify__help">
                {t("react_portal_verify_contact")
                  .replace("%n", contact.name)
                  .replace("%e", contact.email)}
              </p>
            ) : (
              <p className="portal-verify__help">{t("react_portal_verify_not_your_email")}</p>
            )}
          </>
        ) : (
          <>
            <p className="portal-verify__sent">
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
              {/* The prototype names a masked address here. We cannot: the server sends none, and
                  inventing one would confirm the guid is real to anyone holding the link. This
                  says the same useful thing — go and look in your email — and nothing else. On a
                  repeat request the prototype's "request N of 5" takes over, which we can match
                  because the count is ours. */}
              {codeRequestCount > 1
                ? t("react_portal_code_resent")
                    .replace("%n", String(codeRequestCount))
                    .replace("%m", String(MAX_CODE_REQUESTS))
                : maskedEmail
                  ? t("react_portal_code_sent_to").replace("%s", maskedEmail)
                  : t("react_portal_code_sent")}
            </p>

            <p className="portal-verify__prompt">{t("react_portal_enter_code")}</p>

            <input
              type="text"
              className="portal-verify__code"
              value={code}
              onChange={(event) => onCodeChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && canSubmit) onVerify(code);
              }}
              // `inputMode` brings up the number pad on a phone without rejecting a paste, which
              // `type="number"` would also do while adding spinners nobody wants.
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={CODE_LENGTH}
              placeholder="000000"
              // The browser and the OS can offer the code straight from the message.
              autoComplete="one-time-code"
              aria-label={t("react_portal_enter_code")}
              autoFocus
            />

            {error && (
              <p className="portal-verify__error" role="alert">
                {error}
              </p>
            )}

            <button
              type="button"
              className="portal-verify__btn"
              disabled={!canSubmit}
              onClick={() => onVerify(code)}
            >
              {t(isBusy ? "react_loading" : "react_portal_verify_continue")}
            </button>

            <p className="portal-verify__help">
              {t("react_portal_no_code")}{" "}
              <button type="button" className="portal-verify__link" onClick={onRequestCode}>
                {t("react_portal_resend")}
              </button>
              <br />
              {t("react_portal_code_expiry")}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default PortalVerify;
